import { inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { HttpService, LoggerService, ServiceType } from '@danangelus/ngx-app-core'
import { ReplaySubject } from 'rxjs'
import { User } from '../../models/user.model'
import { SessionService } from './session.service'

const log: LoggerService = LoggerService.get('AuthenticationService')

/**
 * Provides the ability to authenticate a user against the system. This also utilities the session service to keep the front
 * and back end sessions in sync
 *
 * @author Dan Bennett
 */
@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {

    private router = inject(Router)
    private http = inject(HttpService)
    private sessionService = inject(SessionService)

    public static authenticated = new ReplaySubject<User | null>()

    async login(username: string, password: string): Promise<void> {
        log.trace('login', `Logging in user [${username}]...`)

        try {
            const user = await this.http.post(ServiceType.SSO, 'login', { username, password })
            if (!user || !user.ssoTicketId) {
                log.error(`Error logging in user [${username}]!`)
            }
            AuthenticationService.authenticated.next(user)
        } catch (error) {
            log.error(`Error logging in user [${username}]! ${error}`)
        }

    }

    async logout(redirectToLogin = true): Promise<void> {
        log.trace('logout', 'Logging out!')

        await this.sessionService.invalidateSession()

        // WARNING: authenticated Subject listeners can try to access backend so make sure old token is removed before notifying them with next.
        AuthenticationService.authenticated.next(null)
        this.sessionService.loggedOut.set(true)

        if (redirectToLogin) {
            await this.router.navigate(['login'])
        }
    }
}
